import { useEffect, useState } from 'react';
import { Loader2, ShieldCheck } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { cn } from '../../../lib/cn';
import { useNotifications } from '../../../store/useStore';
import { emailService } from '../../../lib/email/emailService';
import { listeService } from '../../../lib/email/listeService';
import { alternaLista } from '../../../lib/email/pubblico';
import { SceltaBaseConsenso } from '../SceltaBaseConsenso';
import type { EmailImportBasis, EmailLista } from '../../../types/email.types';

interface NuovoContattoDialogProps {
  aperta: boolean;
  liste: EmailLista[];
  /** Lista gia' spuntata all'apertura: quella che si sta guardando. */
  listaProposta: string | null;
  onChiudi: () => void;
  onAggiunto: () => void;
}

const FONTE_PREDEFINITA = 'Inserito a mano';

/**
 * Aggiunge un indirizzo senza preparare un file. Passa dalla stessa funzione
 * dell'importazione: anche un contatto solo lascia la prova di perche' gli si
 * puo' scrivere.
 */
export function NuovoContattoDialog({
  aperta,
  liste,
  listaProposta,
  onChiudi,
  onAggiunto,
}: NuovoContattoDialogProps) {
  const { addNotification } = useNotifications();

  const [email, setEmail] = useState('');
  const [nome, setNome] = useState('');
  const [azienda, setAzienda] = useState('');
  const [fonte, setFonte] = useState(FONTE_PREDEFINITA);
  const [base, setBase] = useState<EmailImportBasis | null>(null);
  const [scelte, setScelte] = useState<string[]>([]);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!aperta) return;
    setEmail('');
    setNome('');
    setAzienda('');
    setFonte(FONTE_PREDEFINITA);
    setBase(null);
    setScelte(listaProposta ? [listaProposta] : []);
  }, [aperta, listaProposta]);

  const puoSalvare = Boolean(email.trim() && fonte.trim() && base) && !salvando;

  const salva = async () => {
    if (!puoSalvare || !base) return;
    setSalvando(true);
    try {
      const esito = await emailService.addContact(
        { email: email.trim(), nome: nome.trim() || undefined, azienda: azienda.trim() || undefined },
        fonte.trim(),
        base
      );

      if (esito.scartati > 0) {
        addNotification({
          type: 'error',
          title: 'Indirizzo non valido',
          message: `"${email.trim()}" non sembra un indirizzo email.`,
        });
        return;
      }

      for (const listaId of scelte) {
        await listeService.aggiungiPerIndirizzo(listaId, [email]);
      }

      addNotification(
        esito.bloccati > 0
          ? {
              type: 'warning',
              title: 'Indirizzo bloccato',
              message: `${email.trim()} si era disiscritto o era rimbalzato: e' in rubrica ma non ricevera' niente.`,
            }
          : {
              type: 'success',
              title: esito.nuovi > 0 ? 'Contatto aggiunto' : 'Contatto aggiornato',
              message:
                scelte.length > 0
                  ? `${email.trim()} e' nella rubrica e nelle liste scelte.`
                  : `${email.trim()} e' nella rubrica.`,
            }
      );
      onAggiunto();
      onChiudi();
    } catch (errore) {
      addNotification({
        type: 'error',
        title: 'Non riesco ad aggiungere il contatto',
        message: errore instanceof Error ? errore.message : 'Errore imprevisto.',
      });
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog open={aperta} onOpenChange={(v) => !v && onChiudi()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nuovo contatto</DialogTitle>
          <DialogDescription>
            Per aggiungere uno o due indirizzi senza preparare un file. Se l'indirizzo c'e' gia',
            vengono aggiornati i suoi dati.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label htmlFor="nc-email" className="text-xs">
                Indirizzo email <span className="text-primary-600">*</span>
              </Label>
              <Input
                id="nc-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nome@azienda.it"
                className="mt-1"
                autoFocus
              />
            </div>
            <div>
              <Label htmlFor="nc-nome" className="text-xs">
                Nome e cognome
              </Label>
              <Input id="nc-nome" value={nome} onChange={(e) => setNome(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="nc-azienda" className="text-xs">
                Azienda
              </Label>
              <Input
                id="nc-azienda"
                value={azienda}
                onChange={(e) => setAzienda(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>

          <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
            <ShieldCheck className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-900 leading-relaxed">
              Le due risposte qui sotto restano registrate accanto al contatto e non si possono piu'
              modificare: sono la prova da mostrare se qualcuno contesta.
            </p>
          </div>

          <div>
            <Label htmlFor="nc-fonte" className="text-xs">
              Da dove arriva <span className="text-primary-600">*</span>
            </Label>
            <Input
              id="nc-fonte"
              value={fonte}
              onChange={(e) => setFonte(e.target.value)}
              placeholder="Es. biglietto da visita alla fiera, telefonata del 12 marzo"
              className="mt-1"
            />
          </div>

          <div>
            <Label className="text-xs">
              Perche' possiamo scrivergli <span className="text-primary-600">*</span>
            </Label>
            <div className="mt-2">
              <SceltaBaseConsenso valore={base} onScegli={setBase} />
            </div>
          </div>

          {liste.length > 0 && (
            <div>
              <Label className="text-xs">Mettilo nelle liste</Label>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {liste.map((l) => {
                  const scelta = scelte.includes(l.id);
                  return (
                    <button
                      key={l.id}
                      type="button"
                      onClick={() => setScelte((prima) => alternaLista(prima, l.id))}
                      className={cn(
                        'px-3 py-1 rounded-full text-xs font-medium border transition-colors',
                        scelta
                          ? 'bg-primary-600 text-white border-primary-600'
                          : 'bg-white text-neutral-600 border-gray-200 hover:border-gray-300'
                      )}
                    >
                      {l.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onChiudi} disabled={salvando}>
            Annulla
          </Button>
          <Button
            onClick={salva}
            disabled={!puoSalvare}
            className="bg-primary-600 hover:bg-primary-700 text-white"
          >
            {salvando && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Aggiungi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
